
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Response} from '../core/Response';
import {FieldModelResponse} from '../core/Response';
import {AccountState} from '../core/AccountState';
import {FieldModelAccountState} from '../core/AccountState';
import {OrderState} from '../core/OrderState';
import {FieldModelOrderState} from '../core/OrderState';

/**
 * AccountStateGetResponse struct
 */
class AccountStateGetResponse extends Response {
  /**
   * Initialize struct
   * @param {!Response=} parent
   * @param {!AccountState=} accountState
   * @param {!Array=} orderStates
   * @constructor
   */
  constructor (parent = new Response(), accountState = new AccountState(), orderStates = []) {
    super()
    super.copy(parent)
    this.AccountState = accountState
    this.OrderStates = orderStates
  }

  /**
   * Copy struct (shallow copy)
   * @this {!AccountStateGetResponse}
   * @param {!AccountStateGetResponse} other Other struct
   * @returns {!AccountStateGetResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.AccountState != null) {
      this.AccountState = AccountState.fromObject(other.AccountState)
    } else {
      this.AccountState = null
    }
    if (other.OrderStates != null) {
      this.OrderStates = []
      for (let item of other.OrderStates) {
        if (item != null) {
          let tempItem
          tempItem = OrderState.fromObject(item)
          this.OrderStates.push(tempItem)
        } else {
          this.OrderStates.push(null)
        }
      }
    } else {
      this.OrderStates = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!AccountStateGetResponse}
   * @returns {!AccountStateGetResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new AccountStateGetResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new AccountStateGetResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!AccountStateGetResponse}
   * @param {!AccountStateGetResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof AccountStateGetResponse)) {
      throw new TypeError('Instance of AccountStateGetResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!AccountStateGetResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      AccountState: ((this.AccountState != null) ? this.AccountState : null),
      OrderStates: ((this.OrderStates != null) ? Array.from(this.OrderStates, item => ((item != null) ? item : null)) : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return AccountStateGetResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!AccountStateGetResponse} other Object value
   * @returns {!AccountStateGetResponse} Created struct
   */
  static fromObject (other) {
    return new AccountStateGetResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!AccountStateGetResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return AccountStateGetResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!AccountStateGetResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6564
  }
}

export { AccountStateGetResponse };

/**
 * Fast Binary Encoding AccountStateGetResponse field model
 */
class FieldModelAccountStateGetResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelResponse(buffer, 4 + 4)
    this.AccountState = new FieldModelAccountState(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.OrderStates = new fbe.FieldModelVector(new FieldModelOrderState(buffer, this.AccountState.fbeOffset + this.AccountState.fbeSize), buffer, this.AccountState.fbeOffset + this.AccountState.fbeSize)
  }

  /**
   * Get the core.Response field model
   * @this {!FieldModelAccountStateGetResponse}
   * @returns {!FieldModelResponse} core.Response field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelAccountStateGetResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelAccountStateGetResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.AccountState.fbeSize + this.OrderStates.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelAccountStateGetResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.AccountState.fbeExtra + this.OrderStates.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelAccountStateGetResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelAccountStateGetResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelAccountStateGetResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6564
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelAccountStateGetResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelAccountStateGetResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.AccountState.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AccountState.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountState.fbeSize

    if ((fbeCurrentSize + this.OrderStates.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.OrderStates.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrderStates.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelAccountStateGetResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelAccountStateGetResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelAccountStateGetResponse}
   * @param {!AccountStateGetResponse} fbeValue Default value, defaults is new AccountStateGetResponse()
   * @returns {!AccountStateGetResponse} AccountStateGetResponse value
   */
  get (fbeValue = new AccountStateGetResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelAccountStateGetResponse}
   * @param {!AccountStateGetResponse} fbeValue AccountStateGetResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.AccountState.fbeSize) <= fbeStructSize) {
      fbeValue.AccountState = this.AccountState.get()
    } else {
      fbeValue.AccountState = new AccountState()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountState.fbeSize

    if ((fbeCurrentSize + this.OrderStates.fbeSize) <= fbeStructSize) {
      this.OrderStates.get(fbeValue.OrderStates)
    } else {
      fbeValue.OrderStates.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrderStates.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelAccountStateGetResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelAccountStateGetResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelAccountStateGetResponse}
   * @param {!AccountStateGetResponse} fbeValue AccountStateGetResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelAccountStateGetResponse}
   * @param {AccountStateGetResponse} fbeValue AccountStateGetResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.AccountState.set(fbeValue.AccountState)
    this.OrderStates.set(fbeValue.OrderStates)
  }
}

export { FieldModelAccountStateGetResponse };

/**
 * Fast Binary Encoding AccountStateGetResponse model
 */
class AccountStateGetResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelAccountStateGetResponse(this.buffer, 4)
  }

  /**
   * Get the AccountStateGetResponse model
   * @this {!AccountStateGetResponseModel}
   * @returns {!FieldModelAccountStateGetResponse} model AccountStateGetResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!AccountStateGetResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!AccountStateGetResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return AccountStateGetResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!AccountStateGetResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelAccountStateGetResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!AccountStateGetResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!AccountStateGetResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!AccountStateGetResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!AccountStateGetResponseModel}
   * @param {!AccountStateGetResponse} value AccountStateGetResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!AccountStateGetResponseModel}
   * @param {!AccountStateGetResponse} value AccountStateGetResponse value, defaults is new AccountStateGetResponse()
   * @return {!object} Deserialized AccountStateGetResponse value and its size
   */
  deserialize (value = new AccountStateGetResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new AccountStateGetResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new AccountStateGetResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!AccountStateGetResponseModel}
   * @param {!number} prev Previous AccountStateGetResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { AccountStateGetResponseModel };
